import React, { useEffect, useState } from 'react';
import {
  Button,
  Divider,
  Group,
  Paper,
  Pill,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { Settings as SettingsIcon, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { Layout } from '../components/Layout.tsx';
import { UpdatePassword } from '../components/UpdatePassword.tsx';
import { PageHeader } from '../components/app/index.ts';
import { systemSettingsAPI } from '../services/api.ts';
import toast from '../lib/toast.ts';

export const Settings: React.FC = () => {
  const { user } = useAuth();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  // Kept in step with the server; the timer field itself is not shown.
  const [, setMaintenanceUntil] = useState('');
  const [exemptEmails, setExemptEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const [activeTab, setActiveTab] = useState<'security' | 'system'>('security');

  useEffect(() => {
    if (user?.role === 'super_admin') {
      fetchSettings();
    }
  }, [user]);

  const fetchSettings = async () => {
    try {
      const response = await systemSettingsAPI.getSettings();
      if (response.success) {
        setMaintenanceMode(response.data.maintenanceMode);
        setMaintenanceMessage(response.data.maintenanceMessage);
        setExemptEmails(response.data.exemptEmails || []);
        if (response.data.maintenanceUntil) {
          const date = new Date(response.data.maintenanceUntil);
          const formatted = date.toISOString().slice(0, 16);
          setMaintenanceUntil(formatted);
        }
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleUpdateSettings = async (updates: any) => {
    setLoading(true);
    try {
      const response = await systemSettingsAPI.updateSettings(updates);
      if (response.success) {
        setMaintenanceMode(response.data.maintenanceMode);
        setMaintenanceMessage(response.data.maintenanceMessage);
        setExemptEmails(response.data.exemptEmails || []);
        if (response.data.maintenanceUntil) {
          const date = new Date(response.data.maintenanceUntil);
          const formatted = date.toISOString().slice(0, 16);
          setMaintenanceUntil(formatted);
        }
        toast.success(response.message || 'Settings updated');
      }
    } catch (error) {
      console.error('Failed to update settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmail = () => {
    if (newEmail && !exemptEmails.includes(newEmail)) {
      const updatedEmails = [...exemptEmails, newEmail];
      handleUpdateSettings({ exemptEmails: updatedEmails });
      setNewEmail('');
    }
  };

  const handleRemoveEmail = (email: string) => {
    const updatedEmails = exemptEmails.filter((e) => e !== email);
    handleUpdateSettings({ exemptEmails: updatedEmails });
  };

  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <Layout>
      <PageHeader
        title="Settings"
        description="Manage your account security and system preferences."
      />

      <Tabs
        value={activeTab}
        onChange={(value) => value && setActiveTab(value as any)}
        keepMounted={false}
      >
        <Tabs.List mb="lg">
          <Tabs.Tab value="security" leftSection={<ShieldCheck size={16} />}>
            Security
          </Tabs.Tab>
          {isSuperAdmin && (
            <Tabs.Tab value="system" leftSection={<SettingsIcon size={16} />}>
              System
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="security">
          <UpdatePassword />
        </Tabs.Panel>

        {isSuperAdmin && (
          <Tabs.Panel value="system">
            <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }}>
              <Stack gap="lg">
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <Stack gap={4}>
                    <Title order={4}>Maintenance Mode</Title>
                    <Text size="sm" c="dimmed">
                      When enabled, non-exempt users will be blocked from
                      accessing the system.
                    </Text>
                  </Stack>
                  <Switch
                    size="md"
                    aria-label="Maintenance Mode"
                    checked={maintenanceMode}
                    disabled={loading}
                    onChange={() =>
                      handleUpdateSettings({
                        maintenanceMode: !maintenanceMode,
                      })
                    }
                  />
                </Group>

                <Divider />

                <Group align="flex-end" gap="sm">
                  <TextInput
                    flex={1}
                    miw={220}
                    label="Maintenance Message"
                    placeholder="Enter maintenance message..."
                    value={maintenanceMessage}
                    onChange={(e) =>
                      setMaintenanceMessage(e.currentTarget.value)
                    }
                  />
                  <Button
                    disabled={loading}
                    onClick={() => handleUpdateSettings({ maintenanceMessage })}
                  >
                    Update Message
                  </Button>
                </Group>

                <Stack gap="sm">
                  <Group align="flex-end" gap="sm">
                    <TextInput
                      flex={1}
                      miw={220}
                      type="email"
                      label="Exempt Emails"
                      placeholder="Add email to exempt list..."
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.currentTarget.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
                    />
                    <Button
                      disabled={loading || !newEmail}
                      onClick={handleAddEmail}
                    >
                      Add Email
                    </Button>
                  </Group>

                  {exemptEmails.length > 0 ? (
                    <Pill.Group>
                      {exemptEmails.map((email) => (
                        <Pill
                          key={email}
                          size="md"
                          withRemoveButton
                          onRemove={() => handleRemoveEmail(email)}
                        >
                          {email}
                        </Pill>
                      ))}
                    </Pill.Group>
                  ) : (
                    <Text size="xs" c="dimmed" fs="italic">
                      No exempt emails added.
                    </Text>
                  )}
                </Stack>
              </Stack>
            </Paper>
          </Tabs.Panel>
        )}
      </Tabs>
    </Layout>
  );
};
